import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus, Trash2, Loader2, PieChart, UserPlus, Edit, AlertCircle, AlertTriangle, CheckCircle, Building2, User } from "lucide-react";
import type { Account, Shareholder, Contact } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ContactSelect } from "@/components/form-fields/contact-select";
import { AccountSelect } from "@/components/form-fields/account-select";
import { ContactFormModal } from "@/components/contact-form-modal";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { buildNavigationUrl } from "@/lib/breadcrumbs";

interface ShareholderWithContact extends Shareholder {
  contact?: Contact | null;
  shareholderAccount?: Account | null;
}

interface ShareholdersPanelProps {
  account: Account;
}

const SHARE_CLASS_OPTIONS = [
  { value: "ordinary", label: "Ordinary" },
  { value: "class_a", label: "Class A" },
  { value: "class_b", label: "Class B" },
  { value: "class_c", label: "Class C" },
  { value: "class_d", label: "Class D" },
  { value: "class_e", label: "Class E" },
  { value: "preference", label: "Preference" },
  { value: "redeemable_preference", label: "Redeemable Preference" },
  { value: "performance", label: "Performance" },
  { value: "employee", label: "Employee" },
];

export function ShareholdersPanel({ account }: ShareholdersPanelProps) {
  const [, navigate] = useLocation();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingShareholder, setEditingShareholder] = useState<ShareholderWithContact | null>(null);
  const [isCreateContactOpen, setIsCreateContactOpen] = useState(false);
  // AC003: Shareholder type — contact or account
  const [shareholderType, setShareholderType] = useState<"contact" | "account">("contact");
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [shareClass, setShareClass] = useState<string>("ordinary");
  const [numberOfShares, setNumberOfShares] = useState<string>("");
  const [ownershipPercentage, setOwnershipPercentage] = useState<string>("");
  const [markAsBeneficialOwner, setMarkAsBeneficialOwner] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // EDG-726 F002: Edit mode composition state
  const [editIsBeneficialOwner, setEditIsBeneficialOwner] = useState(false);
  const [editOriginalIsBeneficialOwner, setEditOriginalIsBeneficialOwner] = useState(false);
  const { toast} = useToast();

  const { data: shareholders = [], isLoading } = useQuery<ShareholderWithContact[]>({
    queryKey: ["/api/crm/accounts", account.id, "shareholders"],
    enabled: account.entityType === "company",
  });

  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", { clientGroupId: account.clientGroupId }],
    enabled: isAddDialogOpen,
  });

  // ISSUE-162: Get existing shareholder contact IDs to prevent duplicates
  // Note: Same contact CAN hold multiple share classes, so we only exclude exact duplicates
  // For simplicity, we prevent the same contact from being added at all (user can edit share class later)
  const existingShareholderContactIds = useMemo(() => {
    return shareholders.map((shareholder) => shareholder.contactId).filter(Boolean) as string[];
  }, [shareholders]);

  // F011: Real-time shareholding validation
  // Threshold: treat 99.99%–100.01% as valid (floating point tolerance)
  const totalOwnership = useMemo(() => {
    return shareholders.reduce((sum, s) => {
      return sum + (s.ownershipPercentage ? parseFloat(s.ownershipPercentage) : 0);
    }, 0);
  }, [shareholders]);

  const ownershipValidation = useMemo(() => {
    if (shareholders.length === 0) return { state: "empty" as const };
    const TOLERANCE = 0.01;
    if (Math.abs(totalOwnership - 100) <= TOLERANCE) {
      return { state: "valid" as const, total: totalOwnership };
    }
    if (totalOwnership > 100 + TOLERANCE) {
      return {
        state: "over" as const,
        total: totalOwnership,
        excess: totalOwnership - 100,
      };
    }
    return {
      state: "under" as const,
      total: totalOwnership,
      remaining: 100 - totalOwnership,
    };
  }, [shareholders, totalOwnership]);

  const addMutation = useMutation({
    mutationFn: async (data: {
      contactId?: string;
      shareholderAccountId?: string;
      contactFullName: string;
      shareClass: string;
      numberOfShares?: string;
      ownershipPercentage?: string;
      appointmentDate?: string;
      markAsBeneficialOwner?: boolean;
    }) => {
      // Add shareholder — either contact or account
      const payload: Record<string, unknown> = {
        shareClass: data.shareClass,
        numberOfShares: data.numberOfShares,
        ownershipPercentage: data.ownershipPercentage,
        appointmentDate: data.appointmentDate,
      };
      if (data.shareholderAccountId) {
        payload.shareholderAccountId = data.shareholderAccountId;
      } else {
        payload.contactId = data.contactId;
      }
      await apiRequest("POST", `/api/crm/accounts/${account.id}/shareholders`, payload);

      // ISSUE-160 FIX: If marked as beneficial owner, create contact-linked beneficial owner record
      // Only applicable for contact shareholders (not account shareholders)
      if (data.markAsBeneficialOwner && data.contactId) {
        await apiRequest("POST", `/api/crm/accounts/${account.id}/people`, {
          contactId: data.contactId,
          role: "beneficial_owner",
          appointmentDate: data.appointmentDate,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "shareholders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "beneficial-owners"] });
      // ISSUE-160 FIX: Invalidate people query since we create beneficial owner in account_people
      if (markAsBeneficialOwner) {
        queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "people"] });
      }
      toast({ title: "Shareholder added", description: markAsBeneficialOwner
        ? "The shareholder has been added and recorded as a beneficial owner."
        : "The shareholder has been added successfully."
      });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to add shareholder", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/accounts/${account.id}/shareholders/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "shareholders"] });
      toast({ title: "Shareholder removed", description: "The shareholder has been removed." });
      setDeleteId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove shareholder", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      shareClass?: string;
      numberOfShares?: string;
      ownershipPercentage?: string;
      appointmentDate?: string;
    }) => {
      const { id, ...updateData } = data;
      await apiRequest("PATCH", `/api/crm/accounts/${account.id}/shareholders/${id}`, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "shareholders"] });
      toast({ title: "Shareholder updated", description: "The shareholder has been updated successfully." });
      handleCloseEditDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update shareholder", variant: "destructive" });
    },
  });

  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
    setShareholderType("contact");
    setSelectedContactId(null);
    setSelectedContact(null);
    setSelectedAccountId(null);
    setShareClass("ordinary");
    setNumberOfShares("");
    setOwnershipPercentage("");
    setMarkAsBeneficialOwner(false);
  };

  const handleAdd = () => {
    if (shareholderType === "contact" && !selectedContactId) return;
    if (shareholderType === "account" && !selectedAccountId) return;

    let contactFullName = "";
    if (shareholderType === "contact") {
      if (selectedContact) {
        contactFullName = `${selectedContact.firstName || ""} ${selectedContact.lastName || ""}`.trim();
      } else {
        const contact = contacts.find(c => c.id === selectedContactId);
        if (contact) {
          contactFullName = `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
        }
      }
    }

    addMutation.mutate({
      contactId: shareholderType === "contact" ? (selectedContactId ?? undefined) : undefined,
      shareholderAccountId: shareholderType === "account" ? (selectedAccountId ?? undefined) : undefined,
      contactFullName,
      shareClass,
      numberOfShares: numberOfShares || undefined,
      ownershipPercentage: ownershipPercentage || undefined,
      markAsBeneficialOwner: shareholderType === "contact" ? markAsBeneficialOwner : false,
    });
  };
  
  const handleContactCreated = (contact: Contact) => {
    setSelectedContactId(contact.id);
    setSelectedContact(contact);
    setIsCreateContactOpen(false);
    // Refresh contacts query to include the new contact
    queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts", { clientGroupId: account.clientGroupId }] });
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingShareholder(null);
    setShareClass("ordinary");
    setNumberOfShares("");
    setOwnershipPercentage("");
    setEditIsBeneficialOwner(false);
    setEditOriginalIsBeneficialOwner(false);
  };

  const handleOpenEdit = async (shareholder: ShareholderWithContact) => {
    setEditingShareholder(shareholder);
    setShareClass(shareholder.shareClass || "ordinary");
    setNumberOfShares(shareholder.numberOfShares || "");
    setOwnershipPercentage(shareholder.ownershipPercentage || "");

    // EDG-726 F002: Check if this contact is also a beneficial owner
    if (shareholder.contactId) {
      try {
        const peopleRes = await apiRequest("GET", `/api/crm/accounts/${account.id}/people?role=beneficial_owner`);
        const people = await peopleRes.json();
        const isBo = people.some(
          (p: any) => p.contactId === shareholder.contactId && !p.ceasedDate,
        );
        setEditIsBeneficialOwner(isBo);
        setEditOriginalIsBeneficialOwner(isBo);
      } catch {
        setEditIsBeneficialOwner(false);
        setEditOriginalIsBeneficialOwner(false);
      }
    }

    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingShareholder) return;

    // EDG-817 P1-C: Use mutateAsync so composition calls only run if the primary update succeeds
    try {
      await updateMutation.mutateAsync({
        id: editingShareholder.id,
        shareClass,
        numberOfShares: numberOfShares || undefined,
        ownershipPercentage: ownershipPercentage || undefined,
      });
    } catch {
      // onError handler in useMutation already shows a toast — abort composition changes
      return;
    }

    // EDG-726 F002: Handle BO composition changes
    if (editingShareholder.contactId) {
      if (editIsBeneficialOwner && !editOriginalIsBeneficialOwner) {
        await apiRequest("POST", `/api/crm/accounts/${account.id}/people`, {
          contactId: editingShareholder.contactId,
          role: "beneficial_owner",
        });
        queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "people"] });
      }
      if (!editIsBeneficialOwner && editOriginalIsBeneficialOwner) {
        const boRes = await apiRequest("GET", `/api/crm/accounts/${account.id}/people?role=beneficial_owner`);
        const boList = await boRes.json();
        const boRow = boList.find(
          (p: any) => p.contactId === editingShareholder.contactId && !p.ceasedDate,
        );
        if (boRow) {
          const today = new Date().toISOString().split("T")[0];
          await apiRequest("PATCH", `/api/crm/accounts/${account.id}/people/${boRow.id}`, {
            ceasedDate: today,
          });
        }
        queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "people"] });
      }
    }
  };

  const formatNumber = (value: string | null) => {
    if (!value) return "-";
    return new Intl.NumberFormat("en-AU").format(parseFloat(value));
  };

  const formatPercentage = (value: string | null) => {
    if (!value) return "-";
    return `${parseFloat(value).toFixed(2)}%`;
  };

  const getShareClassLabel = (value: string) => {
    const option = SHARE_CLASS_OPTIONS.find(opt => opt.value === value);
    return option?.label || value;
  };

  const isBeneficialOwner = (ownershipPct: string | null) => {
    if (!ownershipPct) return false;
    return parseFloat(ownershipPct) >= 25;
  };

  // AC003: Returns display name for either a contact shareholder or account shareholder
  const getShareholderDisplayName = (shareholder: ShareholderWithContact) => {
    // Account shareholder
    if (shareholder.shareholderAccountId) {
      if (shareholder.shareholderAccount) {
        return shareholder.shareholderAccount.legalName || "Unknown Account";
      }
      return "Account Shareholder";
    }
    // Contact shareholder (legacy path)
    if (shareholder.contact) {
      return `${shareholder.contact.firstName || ""} ${shareholder.contact.lastName || ""}`.trim() || "Unknown";
    }
    const contact = contacts.find(c => c.id === shareholder.contactId);
    if (contact) {
      return `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || "Unknown";
    }
    return "Unknown";
  };

  // Keep legacy alias for edit dialog
  const getContactName = (shareholder: ShareholderWithContact) => getShareholderDisplayName(shareholder);

  if (account.entityType !== "company") {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="shareholders-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">SHAREHOLDERS</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="button-add-shareholder"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Shareholder
        </Button>
      </div>

      {shareholders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md bg-white">
          <PieChart className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No shareholders yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Add Shareholder" to add a shareholder to this company
          </p>
        </div>
      ) : (
        <div className="border rounded-md bg-white">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1">Shareholder</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Type</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Share Class</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 text-right">Shares</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 text-right">Ownership %</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shareholders.map((shareholder) => (
                <TableRow
                  key={shareholder.id}
                  className="h-10"
                  data-testid={`row-shareholder-${shareholder.id}`}
                >
                  <TableCell className="px-2 py-1">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const path = shareholder.shareholderAccountId
                            ? `/accounts/${shareholder.shareholderAccountId}`
                            : `/contacts/${shareholder.contactId}`;
                          const url = buildNavigationUrl(path, {
                            from: 'account',
                            fromId: account.id,
                            fromLabel: account.legalName || 'Account',
                          });
                          navigate(url);
                        }}
                        className="text-primary hover:underline text-sm"
                        data-testid={`link-shareholder-${shareholder.id}`}
                      >
                        {getShareholderDisplayName(shareholder)}
                      </button>
                      {isBeneficialOwner(shareholder.ownershipPercentage) && !shareholder.shareholderAccountId && (
                        <Badge
                          variant="secondary"
                          className="bg-orange-500/10 text-orange-700 dark:text-orange-400 text-xs"
                          data-testid={`badge-beneficial-owner-${shareholder.id}`}
                        >
                          Beneficial Owner
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <Badge variant="outline" className="text-xs">
                      {shareholder.shareholderAccountId ? (
                        <span className="flex items-center gap-1"><Building2 className="h-3 w-3" /> Account</span>
                      ) : (
                        <span className="flex items-center gap-1"><User className="h-3 w-3" /> Contact</span>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    <Badge variant="outline" className="text-xs">
                      {getShareClassLabel(shareholder.shareClass)}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm text-right">
                    {formatNumber(shareholder.numberOfShares)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm text-right">
                    {formatPercentage(shareholder.ownershipPercentage)}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEdit(shareholder)}
                        data-testid={`button-edit-shareholder-${shareholder.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(shareholder.id)}
                        data-testid={`button-delete-shareholder-${shareholder.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* F011: Shareholding Validation — header badge + warning banner + progress bar */}
          {shareholders.length > 0 && (
            <div className="mt-4 space-y-2">
              {/* Header row with total badge */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                <span className="text-sm font-medium text-muted-foreground">Total Ownership:</span>
                <div className="flex items-center gap-2">
                  {ownershipValidation.state === "valid" && (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  )}
                  <Badge
                    data-testid="badge-shareholding-total"
                    variant={ownershipValidation.state === "over" ? "destructive" : "secondary"}
                    className={
                      ownershipValidation.state === "valid"
                        ? "bg-green-500/10 text-green-700 dark:text-green-400"
                        : ownershipValidation.state === "under"
                          ? "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400"
                          : ""
                    }
                  >
                    {totalOwnership.toFixed(2)}%
                  </Badge>
                </div>
              </div>

              {/* Progress bar */}
              <div
                data-testid="bar-shareholding-progress"
                className="relative h-2 rounded-full overflow-hidden bg-muted"
                role="progressbar"
                aria-valuenow={Math.min(totalOwnership, 100)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                {/* Green fill up to 100% */}
                <div
                  className="absolute inset-y-0 left-0 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min(totalOwnership, 100)}%`,
                    backgroundColor:
                      ownershipValidation.state === "valid"
                        ? "rgb(22 163 74)"
                        : ownershipValidation.state === "under"
                          ? "rgb(234 179 8)"
                          : "rgb(239 68 68)",
                  }}
                />
              </div>

              {/* Warning/error banner */}
              {ownershipValidation.state === "over" && (
                <Alert
                  variant="destructive"
                  data-testid="alert-shareholding-validation"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Over-allocated</AlertTitle>
                  <AlertDescription>
                    Total shareholding is{" "}
                    <strong>{ownershipValidation.total.toFixed(2)}%</strong> — exceeds
                    100% by{" "}
                    <strong>{ownershipValidation.excess.toFixed(2)}%</strong>
                  </AlertDescription>
                </Alert>
              )}

              {ownershipValidation.state === "under" && (
                <Alert
                  data-testid="alert-shareholding-validation"
                  className="bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-100"
                >
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertTitle>Under-allocated</AlertTitle>
                  <AlertDescription>
                    Total shareholding is{" "}
                    <strong>{ownershipValidation.total.toFixed(2)}%</strong> —{" "}
                    <strong>{ownershipValidation.remaining.toFixed(2)}%</strong> unallocated
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Shareholder</DialogTitle>
            <DialogDescription>
              Select a contact or account and enter shareholding details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* AC003: Toggle between contact and account shareholder */}
            <div className="space-y-2">
              <Label>Shareholder Type</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={shareholderType === "contact" ? "default" : "outline"}
                  onClick={() => { setShareholderType("contact"); setSelectedAccountId(null); }}
                  data-testid="button-shareholder-type-contact"
                >
                  <User className="h-3 w-3 mr-1" />
                  Contact
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={shareholderType === "account" ? "default" : "outline"}
                  onClick={() => { setShareholderType("account"); setSelectedContactId(null); setSelectedContact(null); }}
                  data-testid="button-shareholder-type-account"
                >
                  <Building2 className="h-3 w-3 mr-1" />
                  Account
                </Button>
              </div>
            </div>

            {shareholderType === "contact" ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Contact</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsCreateContactOpen(true)}
                    data-testid="button-create-new-contact"
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    New Contact
                  </Button>
                </div>
                <ContactSelect
                  value={selectedContactId}
                  onValueChange={setSelectedContactId}
                  clientGroupId={account.clientGroupId}
                  excludeContactIds={existingShareholderContactIds}
                  placeholder="Select a contact"
                  data-testid="select-shareholder-contact"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Account</Label>
                <AccountSelect
                  value={selectedAccountId}
                  onValueChange={setSelectedAccountId}
                  clientGroupId={account.clientGroupId}
                  placeholder="Select an account"
                  data-testid="select-shareholder-account"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Share Class</Label>
              <Select value={shareClass} onValueChange={setShareClass}>
                <SelectTrigger data-testid="select-share-class">
                  <SelectValue placeholder="Select share class" />
                </SelectTrigger>
                <SelectContent>
                  {SHARE_CLASS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Number of Shares</Label>
                <Input
                  type="number"
                  value={numberOfShares}
                  onChange={(e) => setNumberOfShares(e.target.value)}
                  placeholder="Enter number"
                  data-testid="input-number-of-shares"
                />
              </div>
              <div className="space-y-2">
                <Label>Ownership %</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={ownershipPercentage}
                  onChange={(e) => setOwnershipPercentage(e.target.value)}
                  placeholder="0.00"
                  data-testid="input-ownership-percentage"
                />
              </div>
            </div>
            {shareholderType === "contact" && (
              <div className="flex items-center space-x-2 pt-2 border-t">
                <Checkbox
                  id="mark-beneficial-owner"
                  checked={markAsBeneficialOwner}
                  onCheckedChange={(checked) => setMarkAsBeneficialOwner(checked === true)}
                  data-testid="checkbox-mark-beneficial-owner"
                />
                <Label
                  htmlFor="mark-beneficial-owner"
                  className="text-sm font-normal cursor-pointer"
                >
                  Also record as a beneficial owner
                </Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={
                (shareholderType === "contact" && !selectedContactId) ||
                (shareholderType === "account" && !selectedAccountId) ||
                addMutation.isPending
              }
              data-testid="button-confirm-add-shareholder"
            >
              {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Shareholder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Shareholder Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Shareholder</DialogTitle>
            <DialogDescription>
              Update the shareholding details for this shareholder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contact</Label>
              <Input
                value={editingShareholder ? getContactName(editingShareholder) : ""}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label>Share Class</Label>
              <Select value={shareClass} onValueChange={setShareClass}>
                <SelectTrigger data-testid="edit-select-share-class">
                  <SelectValue placeholder="Select share class" />
                </SelectTrigger>
                <SelectContent>
                  {SHARE_CLASS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Number of Shares</Label>
                <Input
                  type="number"
                  value={numberOfShares}
                  onChange={(e) => setNumberOfShares(e.target.value)}
                  placeholder="Enter number"
                  data-testid="edit-input-number-of-shares"
                />
              </div>
              <div className="space-y-2">
                <Label>Ownership %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={ownershipPercentage}
                  onChange={(e) => setOwnershipPercentage(e.target.value)}
                  placeholder="e.g., 25.00"
                  data-testid="edit-input-ownership-percentage"
                />
              </div>
            </div>

            {/* EDG-726 F002: Beneficial Owner checkbox in Edit mode */}
            {editingShareholder?.contactId && (
              <div className="flex items-center space-x-2 pt-2 border-t">
                <Checkbox
                  id="edit-mark-beneficial-owner"
                  checked={editIsBeneficialOwner}
                  onCheckedChange={(checked) => setEditIsBeneficialOwner(checked === true)}
                  data-testid="edit-checkbox-also-beneficial-owner"
                />
                <Label htmlFor="edit-mark-beneficial-owner" className="text-sm font-normal cursor-pointer">
                  Also a Beneficial Owner
                </Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={updateMutation.isPending}
              data-testid="button-confirm-update-shareholder"
            >
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update Shareholder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Shareholder</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this shareholder? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-shareholder"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContactFormModal
        open={isCreateContactOpen}
        onOpenChange={setIsCreateContactOpen}
        onContactCreated={handleContactCreated}
        defaultClientGroupId={account.clientGroupId || undefined}
      />
    </div>
  );
}
